import { createContext } from "react";
import { StorefrontConfig } from "../hooks/useStorefront";

interface User {
  id: number;
  name: string;
  surname?: string;
  phoneNumber?: string;
  avatar?: string;
  cpf?: string;        // MASKED for display only (e.g. '***0190'); full CPF never sent to client
  hasCpf?: boolean;    // whether a CPF is on file (use this, not cpf, for "is set" checks)
  email?: string;
  username?: string;
  nickName?: string;
  profileName?: string;
  birthday?: string;
  followerCount?: number;
  followingCount?: number;
  postCount?: number;
  hideBadges?: boolean;
  hideStatistics?: boolean;
  isGuest?: boolean; // 标识是否为临时用户（未登录）
  is_affiliate_approved?: boolean; // True iff this user has an approved Affiliate account
  is_affiliate_master?: boolean;   // True iff this user owns an approved MASTER/house Affiliate — gates /operacao access
  referral_code?: string;           // MLM referral code (Bundle 4c)
  has_visited_referrals?: boolean;  // False = show avatar badge (Bundle 4c)
  giftingEnabled?: boolean;         // Server's gifting_enabled (GET /api/user/info/). Untrustworthy until userInfoResolved — read via useGiftingGate()
}

interface ScreenSize {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

const Contexts = createContext<{
  globalMessage: string;
  globalMessageColor: "success" | "danger";
  setGlobalMessage: (message: string, color?: "success" | "danger") => void;
  globalMessageAction: (() => void) | null;
  setGlobalMessageAction: (action: (() => void) | null) => void;
  appLoading: boolean;
  setAppLoading: (loading: boolean) => void;
  isNavOpen: boolean;
  setNavVisible: (visible: boolean) => void;
  isLoginOpen: boolean;
  setIsLoginOpen: (open: boolean) => void;
  user: User | null;
  setUser: (user: User | null) => void;
  isRegistrationOpen: boolean;
  setIsRegistrationOpen: (open: boolean) => void;
  userId: number | null;
  setUserId: (id: number | null) => void;
  cartCount: number;
  setCartCount: (count: number) => void;
  showFilter: boolean;
  setShowFilter: (show: boolean) => void;
  isDrawerOpen: boolean;
  setIsDrawerOpen: (open: boolean) => void;
  publicDrawerKey: string;
  setPublicDrawerKey: (key: string) => void;
  drawerData: any;
  setDrawerData: (data: any) => void;
  notificationsCount: number;
  setNotificationsCount: (count: number) => void;
  screenSize: ScreenSize;
  setErrorMessage: (error: any) => void;
  errorMessage: any;
  globalGameFilter: any;
  setGlobalGameFilter: (filter: any) => void;
  postLoginRedirect: string | null;
  setPostLoginRedirect: (path: string | null) => void;
  storefront: StorefrontConfig | null;
  giftingEnabled: boolean;
  /**
   * True once GET /api/user/info/ has SETTLED — not "we have a user". It flips in every
   * terminal branch of fetchUserData (403-anon, guest, authed, error), because a signal
   * that only fires on success would never flip for a logged-out visitor and would hang
   * any guard waiting on it forever. Monotonic: never reset, not even on logout.
   *
   * Until this is true, `giftingEnabled` above is untrustworthy — it rides the 7-day
   * localStorage user cache and can carry a value baked by a previous build. Read the
   * pair through `useGiftingGate()` (utils/giftingFlag), never separately.
   */
  userInfoResolved: boolean;
  setUserInfoResolved: (resolved: boolean) => void;
}>({
  globalMessage: "",
  globalMessageColor: "success",
  setGlobalMessage: () => {},
  globalMessageAction: null,
  setGlobalMessageAction: () => {},
  appLoading: true,
  setAppLoading: () => {},
  isNavOpen: true,
  setNavVisible: () => {},
  isLoginOpen: false,
  setIsLoginOpen: () => {},
  user: null,
  setUser: () => {},
  isRegistrationOpen: false,
  setIsRegistrationOpen: () => {},
  userId: null,
  setUserId: () => {},
  cartCount: 0,
  setCartCount: () => {},
  notificationsCount: 0,
  setNotificationsCount: () => {},
  showFilter: false,
  setShowFilter: () => {},
  isDrawerOpen: false,
  setIsDrawerOpen: () => {},
  publicDrawerKey: "",
  setPublicDrawerKey: () => {},
  drawerData: null,
  setDrawerData: () => {},
  screenSize: {
    isMobile: false,
    isTablet: false,
    isDesktop: false,
  },
  setErrorMessage: () => {},
  errorMessage: null,
  globalGameFilter: null,
  setGlobalGameFilter: () => {},
  postLoginRedirect: null,
  setPostLoginRedirect: () => {},
  storefront: null,
  giftingEnabled: false,
  userInfoResolved: false,
  setUserInfoResolved: () => {},
});

export default Contexts;
